// Demo/test implementation of DialerApi: same rules as the SQL RPCs, via the TS mirrors
// (queueModel → leadQueue.queueOrderKey for eligibility and order, outcomeEffects for the outcome).
import { DEFAULT_GAVION_CONFIG, type GavionConfig } from "../../domain/config";
import { nextCallingWindow, weekdayCallback } from "../../domain/time";
import {
  addDays,
  torontoDate,
  torontoMidnight,
} from "../../domain/time/toronto"; // GAVION callbacks today
import {
  contactCallBlockReason,
  pickNext,
  previewQueue,
  specificBlockReason,
  testModeOn,
  type QueueContext,
} from "../../domain/dialer/queueModel";
import {
  DIAL_OUTCOMES,
  validateOutcome,
  type OutcomePayload,
} from "../../domain/dialer/outcomes";
import { outcomeEffects } from "../../domain/dialer/outcomeEffects";
import { leadStatusesFromConfigRows } from "../../domain/leads/leadStatusModel";
// GAVION contact-call
import {
  contactDisplayName,
  contactLabel,
  demoLinked,
  findContactByEmail,
  findContactPhone,
  isAttendeeEmail,
  offMainLine,
  splitAttendeeName,
} from "../../domain/dialer/contactCall";
import { queueFilterOptions } from "../../domain/dialer/filterOptions";
import type {
  CallOutcome,
  LeadSnapshot,
  QueueLead,
  SessionFilter,
} from "../../domain/dialer/types";
import type { SalesDialPrefs } from "../provider/selectProvider";
import type { RecordStore } from "./recordStore";
import {
  DialerApiError,
  type ActivityItem,
  type CalledContact,
  type CallRow,
  type ContactCallTarget,
  type ClaimResult,
  type DialSession,
  type DialerApi,
  type InboundEndResult, // GAVION inbound calls
  type InboundStartResult, // GAVION inbound calls
} from "./types";
import { briefSnapshot } from "../../ai/brief/callBriefModel"; // GAVION call-brief-pipeline
// GAVION inbound calls
import { normalizePhone } from "../../domain/phone";
import {
  inboundContactName,
  inboundMatches,
} from "../../domain/dialer/inboundCall";

type Company = Partial<QueueLead> &
  Record<string, unknown> & {
    id: number;
    name: string;
    email?: string | null;
    phone_e164?: string | null;
  };
type Opts = {
  store: RecordStore;
  me: number;
  isAdmin?: boolean;
  now?: () => Date;
  config?: GavionConfig;
};
type Suppression = { channel: string; value: string };
/** GAVION requeue fix: twin of public.dialer_skips (one row per rep and lead, the last main-line « Passer »). */
type DialerSkip = {
  id: number;
  sales_id: number;
  company_id: number;
  skipped_at: string;
};
/** The day a skip lasts (SQL: private.queue_preview, start of p_now's Toronto day). */
const SKIP_DAY_TZ = "America/Toronto";
const skipDay = (d: Date) =>
  d.toLocaleDateString("en-CA", { timeZone: SKIP_DAY_TZ });

const EDIT_WINDOW_MS = 24 * 3600 * 1000;
// GAVION inbound calls: SQL start_inbound_call / end_inbound_call windows (80_gavion_inbound_calls.sql)
const INBOUND_REUSE_MS = 2 * 60_000;
const INBOUND_ADOPT_MS = 30 * 60_000;
const INBOUND_END_MS = 4 * 3600_000;
/** pre-deploy review minor 1: a pick on a call whose result was logged in the last 15 min → already_logged. */
const INBOUND_LOGGED_MS = 15 * 60_000;
type InboundContact = {
  id: number;
  company_id?: unknown;
  first_name?: string | null;
  last_name?: string | null;
  phone_jsonb?: unknown;
};
const PREF_KEYS: readonly string[] = [
  "dial_uri_scheme",
  "sip_server",
  "auto_dial",
  "predial_seconds",
  "softphone_verified_at",
  "granola_on_call", // GAVION granola-calls (SQL twin: set_my_dial_prefs in 50_gavion_granola_calls.sql)
  "granola_by_softphone", // GAVION inbound calls (SQL twin: set_my_dial_prefs in 80_gavion_inbound_calls.sql)
];
const HUMAN: readonly string[] = DIAL_OUTCOMES.filter(
  (o) => o.reachedHuman,
).map((o) => o.code);
type FakeContact = {
  id: number;
  company_id?: unknown;
  first_name?: string | null;
  last_name?: string | null;
  email_jsonb?: unknown;
};

type FakeSales = {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
};
/** GAVION skip-callback: the note's reason (SQL twin: public.skip_callback, 93_). */
const SKIP_CALLBACK_NOTE_LABELS: Record<string, string> = {
  inaccurate: "inexact",
  already_done: "déjà fait",
  not_relevant: "plus pertinent",
};
/** « 2030-01-08 à 14:00 » in Toronto (SQL: to_char(... at time zone 'America/Toronto', 'YYYY-MM-DD "à" HH24:MI')). */
const skipNoteDate = (at: string) => {
  const d = new Date(at);
  const day = d.toLocaleDateString("en-CA", { timeZone: "America/Toronto" });
  const time = d.toLocaleTimeString("en-CA", {
    timeZone: "America/Toronto",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  return `${day} à ${time}`;
};

export function createFakeDialerApi({
  store,
  me,
  isAdmin = false,
  now = () => new Date(),
  config = DEFAULT_GAVION_CONFIG,
}: Opts): DialerApi {
  const tz = config.calling_hours.timezone_default;
  const iso = (d: Date) => d.toISOString();
  const leaseEnd = () =>
    iso(new Date(now().getTime() + config.lease_minutes * 60_000));
  // GAVION lead statuses: configuration row 1's list (a store without it reads the defaults)
  const leadStatuses = async () =>
    leadStatusesFromConfigRows(await store.list("configuration"));
  // GAVION inbound calls: twin of private.inbound_call_result's status and matches (a call still without a lead)
  const inboundResult = async (status: string, call: CallRow) => {
    const phone = call.from_number ?? "";
    const row = await inboundRow(call);
    if (call.company_id != null) return { status, phone, call: row };
    const matches = inboundMatches(
      phone,
      await store.list<Company>("companies"),
      await store.list<InboundContact>("contacts"),
    );
    const leads = new Set(matches.map((m) => m.company_id)).size;
    return {
      status:
        status === "pending" ? (leads > 1 ? "choose" : "unknown") : status,
      phone,
      call: row,
      matches,
    };
  };
  // GAVION inbound calls: twin of private.inbound_call_result (the lead's name, zone and email, the contact's name)
  const inboundRow = async (call: CallRow): Promise<CallRow> => {
    const c =
      call.company_id == null
        ? null
        : await store.get<Company>("companies", call.company_id);
    const k =
      call.contact_id == null
        ? null
        : await store.get<InboundContact>("contacts", call.contact_id);
    return {
      ...call,
      company_name: c?.name ?? null,
      company_timezone: (c?.timezone as string | null | undefined) ?? null,
      company_email: c?.email ?? null,
      contact_name: k ? inboundContactName(k) : null,
    };
  };

  const asLead = (c: Company): QueueLead => ({
    id: Number(c.id),
    phone_e164: c.phone_e164 ?? null,
    phone_valid: c.phone_valid ?? true,
    prospect_status: c.prospect_status ?? "new",
    attempt_count: c.attempt_count ?? 0,
    next_callback_at: c.next_callback_at ?? null,
    cooldown_until: c.cooldown_until ?? null,
    assigned_sales_id: c.assigned_sales_id ?? null,
    locked_by: c.locked_by ?? null,
    locked_until: c.locked_until ?? null,
    last_called_at: c.last_called_at ?? null,
    high_quality: c.high_quality ?? false,
    fit_score: c.fit_score ?? null,
    date_added: c.date_added ?? null,
    timezone: c.timezone ?? tz,
    trade: c.trade ?? null,
    region: c.region ?? null,
    ...(c.is_test === true ? { is_test: true } : {}), // GAVION test mode
  });

  const ctx = async (filter: SessionFilter): Promise<QueueContext> => {
    const sup = await store.list<Suppression>("suppressions");
    return {
      me,
      now: now(),
      maxAttempts: config.max_attempts,
      filter,
      hours: config.calling_hours,
      defaultTimezone: tz,
      admin: isAdmin, // GAVION test mode (SQL private.test_mode_admin)
      suppressedPhones: new Set(
        sup.filter((s) => s.channel === "phone").map((s) => s.value),
      ),
    };
  };

  /** GAVION requeue fix: leads I skipped today (Toronto day), left out of my queue like SQL queue_preview. */
  const skippedToday = async (): Promise<ReadonlySet<number>> => {
    const today = skipDay(now());
    return new Set(
      (await store.list<DialerSkip>("dialer_skips"))
        .filter(
          (x) => x.sales_id === me && skipDay(new Date(x.skipped_at)) === today,
        )
        .map((x) => x.company_id),
    );
  };
  /** Twin of log_call_outcome's [REQUEUE] block: insert, or move the time of my row for that lead. */
  const rememberSkip = async (companyId: number) => {
    const at = iso(now());
    const row = (await store.list<DialerSkip>("dialer_skips")).find(
      (x) => x.sales_id === me && x.company_id === companyId,
    );
    if (row) await store.update("dialer_skips", row.id, { skipped_at: at });
    else
      await store.create("dialer_skips", {
        sales_id: me,
        company_id: companyId,
        skipped_at: at,
      });
  };
  /** The queue's candidates: every lead but the excluded ones and those I skipped today. */
  // GAVION test mode: in « Mode test » today's « Passer » does not hide a test lead (SQL queue_preview, 58_)
  const queueLeads = async (
    exclude: readonly number[] = [],
    filter: SessionFilter = {},
  ) => {
    const skipped = await skippedToday();
    const test = testModeOn({ admin: isAdmin, filter });
    return (await store.list<Company>("companies"))
      .map(asLead)
      .filter(
        (l) =>
          !exclude.includes(l.id) &&
          (!skipped.has(l.id) || (test && l.is_test === true)),
      );
  };

  const active = async () =>
    (await store.list<DialSession>("dial_sessions")).find(
      (s) => s.sales_id === me && !s.ended_at,
    ) ?? null;

  const mustSession = async (id: number) => {
    const s = await store.get<DialSession>("dial_sessions", id);
    if (!s || s.sales_id !== me || s.ended_at)
      throw new DialerApiError("session_not_active");
    return s;
  };

  const releaseMine = async (keep?: number) => {
    for (const c of await store.list<Company>("companies")) {
      if (c.locked_by === me && c.id !== keep)
        await store.update("companies", c.id, {
          locked_by: null,
          locked_until: null,
        });
    }
  };

  // GAVION demo-contact (SQL twin: private.upsert_demo_contact, 68_): the lead's contact with that address, else a
  // new one (no phone, the lead's language, my contact); a name typed fills a nameless contact, never renames one;
  // an erased / suppressed address is never inserted again (null).
  const upsertDemoContact = async (
    company: Company,
    email: string,
    rawName: string | undefined,
  ): Promise<number | null> => {
    const name = splitAttendeeName(rawName);
    const found = findContactByEmail(
      await store.list<FakeContact>("contacts"),
      company.id,
      email,
    );
    if (found) {
      if (name.first_name && !contactDisplayName(found))
        await store.update("contacts", found.id, name);
      return found.id;
    }
    if (
      (await store.list<Suppression>("suppressions")).some(
        (x) => x.channel === "email" && x.value === email,
      )
    )
      return null;
    const t = iso(now());
    const created = await store.create<FakeContact>("contacts", {
      ...name,
      company_id: company.id,
      sales_id: me,
      email_jsonb: [{ email, type: "Work" }],
      phone_jsonb: [],
      language: (company.language as string) ?? "fr",
      tags: [],
      first_seen: t,
      last_seen: t,
    });
    return created.id;
  };

  const snapshot = async (id: number): Promise<LeadSnapshot> => {
    const c = (await store.get<Company>("companies", id))!;
    const contact = (
      await store.list<Record<string, unknown>>("contacts")
    ).find((x) => x.company_id === id);
    const owner =
      c.assigned_sales_id != null
        ? await store.get<{ first_name: string; last_name: string }>(
            "sales",
            c.assigned_sales_id,
          )
        : null;
    return {
      ...asLead(c),
      name: c.name,
      city: (c.city as string) ?? null,
      website: (c.website as string) ?? null,
      email: c.email ?? null,
      language: (c.language as "fr" | "en") ?? "fr",
      last_call_outcome: (c.last_call_outcome as CallOutcome) ?? null,
      contact_name: contact ? contactLabel(contact) || null : null, // GAVION demo-contact: else his email
      contact_role: (contact?.role_title as string) ?? null,
      assigned_name: owner ? `${owner.first_name} ${owner.last_name}` : null,
    };
  };

  const lease = async (
    companyId: number,
    session: DialSession,
    assign: boolean,
    contact: CalledContact | null = null, // GAVION contact-call
  ): Promise<ClaimResult> => {
    const c = (await store.get<Company>("companies", companyId))!;
    const t = iso(now());
    await store.update("companies", companyId, {
      locked_by: me,
      locked_until: leaseEnd(),
      assigned_sales_id: c.assigned_sales_id ?? (assign ? me : null),
    });
    const call = await store.create<CallRow>("calls", {
      company_id: companyId,
      contact_id: contact?.id ?? null,
      sales_id: me,
      dial_session_id: session.id,
      direction: "outbound",
      to_number: contact ? contact.phone_e164 : (c.phone_e164 ?? null),
      claimed_at: t,
      dialed_at: t,
      outcome: null,
      outcome_version: 0,
      outcome_at: null,
      note: null,
      callback_at: null,
      voipms_cdr_id: null,
      deal_id: null,
      duration_s: null,
      disposition: null,
    });
    // fresh count: close_open_calls may just have taken a dial back
    const fresh = await store.get<DialSession>("dial_sessions", session.id);
    await store.update("dial_sessions", session.id, {
      dials: (fresh?.dials ?? session.dials) + 1,
    });
    return {
      reason: "ok",
      session_id: session.id,
      company_id: companyId,
      call_id: call.id,
      lead: await snapshot(companyId),
      to_number: call.to_number,
      resumed_at: null,
      ...(contact ? { contact } : {}),
    };
  };

  /**
   * Review fix M3, twin of SQL private.close_open_calls: the rep's open calls on a held lead, closed like a « Passer »
   * when a manual dial switches target (no CDR → dropped with its session dial; a CDR → kept as skipped).
   */
  const closeOpenCalls = async (companyId: number) => {
    const open = (await store.list<CallRow>("calls")).filter(
      (x) =>
        x.company_id === companyId && x.sales_id === me && x.outcome === null,
    );
    for (const x of open) {
      if (x.voipms_cdr_id) {
        await store.update("calls", x.id, {
          outcome: "skipped",
          outcome_at: iso(now()),
        });
        continue;
      }
      await store.remove("calls", x.id);
      if (x.dial_session_id == null) continue;
      const s = await store.get<DialSession>(
        "dial_sessions",
        x.dial_session_id,
      );
      if (s)
        await store.update("dial_sessions", s.id, {
          dials: Math.max(s.dials - 1, 0),
        });
    }
  };

  /** My open main-line call on a lead, newest first (review fix I1: never an open contact call). */
  const openMainCall = async (companyId: number) =>
    (await store.list<CallRow>("calls"))
      .filter(
        (x) =>
          x.company_id === companyId &&
          x.sales_id === me &&
          x.outcome === null &&
          !x.contact_id,
      )
      .sort((a, b) => b.id - a.id)[0];

  /** GAVION contact-call: twin of SQL claim_contact_call (after the session checks). */
  const claimContact = async (
    s: DialSession,
    c: Company,
    target: ContactCallTarget,
  ): Promise<ClaimResult> => {
    const k = await store.get<Record<string, unknown>>(
      "contacts",
      target.contactId,
    );
    if (!k || k.company_id !== c.id) throw new DialerApiError("not_found");
    const phone = findContactPhone(k.phone_jsonb, target.phone);
    if (!phone) return empty(s.id, "invalid_phone");
    const reason = contactCallBlockReason(asLead(c), phone.e164, await ctx({}));
    if (reason === "locked") {
      const holder = await store.get<{ first_name: string }>(
        "sales",
        c.locked_by as number,
      );
      return empty(s.id, reason, {
        locked_by_name: holder?.first_name ?? null,
      });
    }
    if (reason) return empty(s.id, reason);
    const t = now().getTime();
    const warning =
      c.cooldown_until && Date.parse(c.cooldown_until) > t
        ? ("cooling_down" as const)
        : c.assigned_sales_id != null && c.assigned_sales_id !== me
          ? ("assigned_to_other" as const)
          : null;
    const contact: CalledContact = {
      id: Number(k.id),
      name: contactDisplayName(
        k as { first_name?: string; last_name?: string },
      ),
      title: (k.title as string) || null,
      phone_e164: phone.e164,
      phone_type: phone.type,
    };
    const held =
      c.locked_by === me && !!c.locked_until && Date.parse(c.locked_until) >= t;
    const open = held
      ? (await store.list<CallRow>("calls"))
          .filter(
            (x) =>
              x.company_id === c.id &&
              x.sales_id === me &&
              x.outcome === null &&
              x.contact_id === contact.id &&
              x.to_number === contact.phone_e164,
          )
          .sort((a, b) => b.id - a.id)[0]
      : undefined;
    if (open)
      return {
        reason: "ok",
        session_id: s.id,
        company_id: c.id,
        call_id: open.id,
        lead: await snapshot(c.id),
        to_number: open.to_number,
        warning,
        contact,
      };
    if (held) await closeOpenCalls(c.id); // review fix M3
    await releaseMine(c.id);
    return { ...(await lease(c.id, s, false, contact)), warning };
  };

  const empty = (
    session_id: number,
    reason: ClaimResult["reason"],
    extra: Partial<ClaimResult> = {},
  ): ClaimResult => ({
    reason,
    session_id,
    company_id: null,
    call_id: null,
    lead: null,
    ...extra,
  });

  const api: DialerApi = {
    getActiveSession: active,

    async startSession(raw) {
      // GAVION test mode (SQL start_dial_session): test_mode = true for an admin only, a rep is refused
      const { test_mode, ...rest } = raw;
      if (test_mode === true && !isAdmin) throw new DialerApiError("forbidden");
      const filter: SessionFilter =
        test_mode === true ? { ...rest, test_mode: true } : rest;
      const stale = await active();
      if (stale) await api.endSession(stale.id);
      return store.create<DialSession>("dial_sessions", {
        sales_id: me,
        mode: "A",
        started_at: iso(now()),
        ended_at: null,
        paused: false,
        dials: 0,
        contacts: 0,
        demos: 0,
        filter,
      });
    },

    async endSession(id) {
      await mustSession(id);
      await releaseMine();
      await store.update("dial_sessions", id, { ended_at: iso(now()) });
    },

    async pauseSession(id, paused) {
      await mustSession(id);
      await store.update("dial_sessions", id, { paused });
    },

    async claimNext(sessionId, exclude = []) {
      const s = await mustSession(sessionId);
      if (s.paused) return empty(s.id, "session_paused");
      // GAVION dialer pin (SQL claim_next_lead, 64_): the lead asked for with « Appeler » stays my card until its
      // result or « Passer » — served again like a held queue lead, exclude ignored; failing the manual checks unpins it
      if (s.pinned_company_id != null) {
        const pinned = await store.get<Company>(
          "companies",
          s.pinned_company_id,
        );
        if (pinned && !specificBlockReason(asLead(pinned), await ctx({}))) {
          await releaseMine(pinned.id);
          const open = await openMainCall(pinned.id);
          if (!open) {
            // swept (unlogged) or closed by a contact call: a new row, still reported as already called (my last
            // call on the lead), so a re-serve is never auto-dialled, even cooling down
            const last = (await store.list<CallRow>("calls"))
              .filter((x) => x.company_id === pinned.id && x.sales_id === me)
              .sort((a, b) => b.id - a.id)[0];
            return {
              ...(await lease(pinned.id, s, false)),
              resumed_at: last ? (last.claimed_at ?? last.dialed_at) : null,
            };
          }
          await store.update("companies", pinned.id, {
            locked_by: me,
            locked_until: leaseEnd(),
          });
          return {
            reason: "ok",
            session_id: s.id,
            company_id: pinned.id,
            call_id: open.id,
            lead: await snapshot(pinned.id),
            to_number: open.to_number,
            resumed_at: open.claimed_at ?? open.dialed_at,
          };
        }
        await store.update("dial_sessions", s.id, { pinned_company_id: null });
      }
      await releaseMine();
      const leads = (await store.list<Company>("companies")).map(asLead);
      const r = pickNext(
        await queueLeads(exclude, s.filter ?? {}),
        await ctx(s.filter ?? {}),
      );
      if (r.lead) {
        // GAVION requeue fix: my open main-line call on that lead is handed back (no new row, no dial)
        const open = (await store.list<CallRow>("calls"))
          .filter(
            (x) =>
              x.company_id === r.lead!.id &&
              x.sales_id === me &&
              x.outcome === null &&
              !x.contact_id,
          )
          .sort((a, b) => b.id - a.id)[0];
        if (!open) return lease(r.lead.id, s, true);
        const c = (await store.get<Company>("companies", r.lead.id))!;
        await store.update("companies", c.id, {
          locked_by: me,
          locked_until: leaseEnd(),
          assigned_sales_id: c.assigned_sales_id ?? me,
        });
        return {
          reason: "ok",
          session_id: s.id,
          company_id: c.id,
          call_id: open.id,
          lead: await snapshot(c.id),
          to_number: open.to_number,
          resumed_at: open.claimed_at ?? open.dialed_at,
        };
      }
      if (r.reason === "outside_calling_hours")
        return empty(s.id, r.reason, {
          next_window: iso(nextCallingWindow(now(), tz, config.calling_hours)),
        });
      const next =
        leads
          .filter(
            (c) =>
              c.next_callback_at &&
              Date.parse(c.next_callback_at) > now().getTime() &&
              (c.assigned_sales_id === me || c.assigned_sales_id === null),
          )
          .map((c) => c.next_callback_at as string)
          .sort()[0] ?? null;
      return empty(s.id, "queue_empty", { next_callback_at: next });
    },

    async claimSpecific(sessionId, companyId, target) {
      const s = sessionId
        ? await mustSession(sessionId)
        : ((await active()) ?? (await api.startSession({})));
      // GAVION dialer pin: a contact call still waits for « Reprendre »; « Appeler » on a lead resumes below
      if (s.paused && target) return empty(s.id, "session_paused");
      const c = await store.get<Company>("companies", companyId);
      if (!c) throw new DialerApiError("not_found");
      if (target) return claimContact(s, c, target); // GAVION contact-call
      const reason = specificBlockReason(asLead(c), await ctx({}));
      if (reason === "locked") {
        const holder = await store.get<{ first_name: string }>(
          "sales",
          c.locked_by as number,
        );
        return empty(s.id, reason, {
          locked_by_name: holder?.first_name ?? null,
        });
      }
      if (reason) return empty(s.id, reason);
      const t = now().getTime();
      // Same order as SQL claim_specific_lead: cooling down first, then assigned to another rep.
      const warning =
        c.cooldown_until && Date.parse(c.cooldown_until) > t
          ? ("cooling_down" as const)
          : c.assigned_sales_id != null && c.assigned_sales_id !== me
            ? ("assigned_to_other" as const)
            : null;
      // GAVION dialer pin (SQL claim_specific_lead, 64_): « Appeler » resumes the session, forgets my « Passer » of the
      // day on the lead and pins it; my open main-line call on it is handed back even after the lease lapsed.
      await store.update("dial_sessions", s.id, {
        paused: false,
        pinned_company_id: companyId,
      });
      for (const k of await store.list<DialerSkip>("dialer_skips"))
        if (k.sales_id === me && k.company_id === companyId)
          await store.remove("dialer_skips", k.id);
      const held =
        c.locked_by === me &&
        !!c.locked_until &&
        Date.parse(c.locked_until) >= t;
      const open = await openMainCall(companyId);
      if (open) {
        await releaseMine(companyId);
        await store.update("companies", companyId, {
          locked_by: me,
          locked_until: leaseEnd(),
        });
        return {
          reason: "ok",
          session_id: s.id,
          company_id: companyId,
          call_id: open.id,
          lead: await snapshot(companyId),
          to_number: open.to_number,
          warning,
        };
      }
      if (held) await closeOpenCalls(companyId); // review fix M3
      await releaseMine(companyId);
      return { ...(await lease(companyId, s, false)), warning };
    },

    async renewLease(companyId) {
      const c = await store.get<Company>("companies", companyId);
      const expired =
        !c?.locked_until || Date.parse(c.locked_until) < now().getTime();
      if (!c || c.locked_by !== me || expired)
        throw new DialerApiError("lease_lost");
      const until = leaseEnd();
      await store.update("companies", companyId, { locked_until: until });
      return until;
    },

    async releaseLease(companyId) {
      const c = await store.get<Company>("companies", companyId);
      if (c && c.locked_by === me)
        await store.update("companies", companyId, {
          locked_by: null,
          locked_until: null,
        });
    },

    // GAVION small fixes (SQL twin: public.mark_call_launched in 52_gavion_small_fixes.sql)
    async markCallLaunched(callId) {
      const call = await store.get<CallRow>("calls", callId);
      if (!call || call.sales_id !== me || call.outcome !== null)
        throw new DialerApiError("not_found");
      if (call.launched_at) return call.launched_at;
      const at = iso(now());
      await store.update("calls", callId, { launched_at: at });
      return at;
    },

    async logOutcome(raw: OutcomePayload) {
      const call = await store.get<CallRow>("calls", raw.call_id);
      if (!call || call.sales_id !== me)
        throw new DialerApiError("call_not_found");
      // GAVION inbound calls (SQL 80 §6): an inbound call was answered
      const inbound = call.direction === "inbound";
      if (
        inbound &&
        ["no_answer", "voicemail", "skipped"].includes(raw.outcome)
      )
        throw new DialerApiError("outcome_invalid");
      // pre-deploy review minor 2: the lead first
      if (inbound && call.company_id == null)
        throw new DialerApiError("lead_required");
      if (raw.version <= (call.outcome_version ?? 0))
        return { call, demo_deal_id: null }; // replay: already applied
      if (
        call.outcome &&
        call.outcome_at &&
        now().getTime() - Date.parse(call.outcome_at) > EDIT_WINDOW_MS
      )
        throw new DialerApiError("edit_window_closed");
      const company = (await store.get<Company>(
        "companies",
        call.company_id as number,
      ))!;
      // GAVION missed-call: like SQL (private.weekday_callback, 55), a future callback on a Saturday, a Sunday, Dec 25
      // or Jan 1 (lead-local date) is moved to the next business day, same local time; validation sees the rep's value.
      const moved =
        raw.callback_at && Date.parse(raw.callback_at) > now().getTime()
          ? weekdayCallback(new Date(raw.callback_at), company.timezone ?? tz)
          : null;
      const p: OutcomePayload =
        moved && moved.getTime() !== Date.parse(raw.callback_at as string)
          ? { ...raw, callback_at: moved.toISOString() }
          : raw;
      const v = validateOutcome(raw, {
        now: now(),
        leadTimezone: company.timezone ?? tz,
        leadEmail: company.email ?? null,
        hours: config.calling_hours,
      });
      if (!v.ok) throw new DialerApiError(v.code, v.suggestion);
      const e = outcomeEffects({
        outcome: p.outcome,
        payload: p,
        previousOutcome: call.outcome,
        dialedAt: call.dialed_at,
        now: now(),
        hasCdr: !!call.voipms_cdr_id,
        // GAVION contact-call: the number dialled when the call went to a contact
        contactPhone:
          call.contact_id && !demoLinked(call) ? call.to_number : null, // GAVION demo-contact: a linked call is main-line
        // follow-up M5: SQL keys on contact_id (v_contact_call), even when to_number is null; 41 adds an outbound
        // call without a number (a deleted contact's open call), never treated as the main line
        contactCall: offMainLine(call),
        statuses: await leadStatuses(), // GAVION lead statuses
        company: {
          attempt_count: company.attempt_count ?? 0,
          prospect_status: company.prospect_status ?? "new",
          next_callback_at: company.next_callback_at ?? null,
          cooldown_until: company.cooldown_until ?? null,
          phone_valid: company.phone_valid ?? true,
          phone_e164: company.phone_e164 ?? null,
          email: company.email ?? null,
        },
      });
      let saved: CallRow | null = null;
      if (e.deleteCall) await store.remove("calls", call.id);
      else
        saved = await store.update<CallRow>("calls", call.id, {
          outcome: p.outcome,
          outcome_version: p.version,
          outcome_at: iso(now()),
          note: p.note ?? null,
          callback_at: p.callback_at ?? null,
          not_interested_reason: p.reason ?? null,
          // GAVION demo-contact: « Passer » keeps the demo (SQL's skipped branch never touches calls.demo)
          demo:
            p.outcome === "skipped" ? (call.demo ?? null) : (p.demo ?? null),
          email: p.email ?? null,
        });
      // GAVION demo-contact (SQL twin: log_call_outcome 68_): the demo's attendee becomes (or is found as) a contact
      // of the lead; a main-line call becomes his (contact_linked), a contact call keeps its contact; an edit that
      // drops the demo, or types an address that gives no contact, unlinks the call; a demo edit that does not retype
      // the address keeps the attendee and his address. « Passer » (skipped) keeps everything, as SQL returns before.
      // The demo has no R6 deal to add him to.
      if (saved && p.outcome !== "skipped") {
        const linked = demoLinked(call);
        const old = (call.demo ?? null) as Record<string, unknown> | null;
        const typed =
          p.outcome === "demo_booked"
            ? (p.demo?.attendee_email ?? "").trim().toLowerCase()
            : "";
        let contactId: number | null = null;
        if (typed) {
          if (isAttendeeEmail(typed))
            contactId = await upsertDemoContact(
              company,
              typed,
              p.demo?.attendee_name,
            );
        } else if (p.outcome === "demo_booked" && old?.contact_id != null) {
          const kept = await store.get<FakeContact>(
            "contacts",
            Number(old.contact_id),
          );
          contactId = kept && kept.company_id === company.id ? kept.id : null;
        }
        if (contactId !== null || linked) {
          const own = call.contact_id == null || linked;
          const demo = (saved.demo ?? {}) as Record<string, unknown>;
          saved = await store.update<CallRow>("calls", call.id, {
            contact_id:
              contactId !== null && own
                ? contactId
                : linked
                  ? null
                  : (call.contact_id ?? null),
            demo:
              contactId !== null
                ? {
                    ...demo,
                    contact_id: contactId,
                    contact_linked: own,
                    ...(demo.attendee_email == null &&
                    old?.attendee_email != null
                      ? { attendee_email: old.attendee_email }
                      : {}),
                  }
                : (saved.demo ?? null),
          });
        }
      }
      // GAVION call-brief-pipeline (SQL twin: private.calls_brief_snapshot, 84_): the call keeps the brief shown before
      // it, once, from its first real result
      if (
        saved &&
        (saved as Record<string, unknown>).brief == null &&
        call.company_id != null &&
        (!call.outcome || call.outcome === "unlogged")
      ) {
        const briefs = await store.list<Record<string, unknown>>("call_briefs");
        const snap = briefSnapshot(
          briefs.find((b) => String(b.company_id) === String(call.company_id)),
        );
        if (snap)
          saved = await store.update<CallRow>("calls", call.id, {
            brief: snap,
          });
      }
      const release =
        e.releaseLease && company.locked_by === me
          ? { locked_by: null, locked_until: null }
          : {};
      await store.update("companies", company.id, {
        ...e.companyPatch,
        ...release,
        // GAVION inbound calls (SQL 80 §6): an inbound call is no attempt
        ...(inbound ? { attempt_count: company.attempt_count ?? 0 } : {}),
      });
      if (call.dial_session_id) {
        const s = await store.get<DialSession>(
          "dial_sessions",
          call.dial_session_id,
        );
        if (s)
          await store.update("dial_sessions", s.id, {
            dials: s.dials + e.session.dials,
            contacts: s.contacts + e.session.contacts,
            demos: s.demos + e.session.demos,
          });
      }
      const existing = await store.list<Suppression>("suppressions");
      // GAVION inbound calls (SQL 80 §6): do_not_call also suppresses the caller's number
      const suppress =
        inbound &&
        p.outcome === "do_not_call" &&
        call.from_number &&
        !e.suppress.some((x) => x.value === call.from_number)
          ? [...e.suppress, { channel: "phone", value: call.from_number }]
          : e.suppress;
      for (const sup of suppress) {
        if (
          !existing.some(
            (x) => x.channel === sup.channel && x.value === sup.value,
          )
        )
          await store.create("suppressions", {
            ...sup,
            reason: "dnc_request",
            source: "dialer",
            company_id: company.id,
            created_by: me,
          });
      }
      // GAVION requeue fix: a main-line « Passer » keeps the lead out of my queue until the next Toronto day
      if (
        p.outcome === "skipped" &&
        !offMainLine(call) &&
        call.company_id != null
      )
        await rememberSkip(call.company_id);
      // GAVION dialer pin: a first main-line result (or « Passer ») on my pinned lead ends the pin (a contact call's
      // does not); the call's note stays on the call (no company_notes copy any more, SQL log_call_outcome 64_)
      if ((!call.outcome || call.outcome === "unlogged") && !offMainLine(call))
        for (const ds of await store.list<DialSession>("dial_sessions"))
          if (ds.sales_id === me && ds.pinned_company_id === company.id)
            await store.update("dial_sessions", ds.id, {
              pinned_company_id: null,
            });
      return {
        call: saved,
        demo_deal_id: e.demoDealPrompt ? (call.deal_id ?? null) : null,
      };
    },

    // GAVION off-CRM call (SQL twin: public.log_off_crm_call, 86_): the call is created, then its result goes through
    // logOutcome; a refused result removes the call (SQL: one transaction).
    async logOffCrmCall(p) {
      const at = Date.parse(p.called_at);
      if (Number.isNaN(at) || at > now().getTime() + 60_000)
        throw new DialerApiError("called_at_future");
      if (at < now().getTime() - 7 * 24 * 3600_000)
        throw new DialerApiError("called_at_too_old");
      if ((p.call.outcome as string) === "skipped")
        throw new DialerApiError("outcome_invalid");
      const company = await store.get<Company>("companies", p.company_id);
      if (!company) throw new DialerApiError("not_found");
      let toNumber: string | null = company.phone_e164 ?? null;
      if (p.contact_id !== null) {
        const k = await store.get<InboundContact>("contacts", p.contact_id);
        if (!k || Number(k.company_id) !== p.company_id)
          throw new DialerApiError("not_found");
        const numbers = Array.isArray(k.phone_jsonb)
          ? (k.phone_jsonb as { number?: string }[])
          : [];
        toNumber =
          numbers
            .map((n) => normalizePhone(n.number ?? ""))
            .find((n): n is string => !!n) ?? null;
      }
      const created = await store.create<CallRow>("calls", {
        company_id: p.company_id,
        contact_id: p.contact_id,
        sales_id: me,
        dial_session_id: null,
        direction: "outbound",
        to_number: toNumber,
        claimed_at: null,
        dialed_at: new Date(at).toISOString(),
        outcome: null,
        outcome_version: 0,
        outcome_at: null,
        note: null,
        callback_at: null,
        voipms_cdr_id: null,
        deal_id: null,
        duration_s: null,
        disposition: null,
        off_crm: true,
      });
      try {
        const res = await api.logOutcome({
          ...p.call,
          call_id: created.id,
          version: 1,
        });
        return res.call ?? created;
      } catch (e) {
        await store.remove("calls", created.id);
        throw e;
      }
    },

    // GAVION skip-callback (SQL twin: public.skip_callback, 93_): the assigned rep or an admin clears the callback;
    // « Rappel » goes back to « En cours »; a history note (source automation).
    async skipCallback(companyId, reason) {
      const label = SKIP_CALLBACK_NOTE_LABELS[reason];
      if (!label) throw new DialerApiError("invalid_reason");
      const company = await store.get<Company>("companies", companyId);
      if (!company) throw new DialerApiError("not_found");
      if (!isAdmin && company.assigned_sales_id !== me)
        throw new DialerApiError("forbidden");
      const at = company.next_callback_at;
      if (!at) throw new DialerApiError("no_callback");
      await store.update("companies", companyId, {
        next_callback_at: null,
        ...(company.prospect_status === "callback"
          ? { prospect_status: "in_progress" }
          : {}),
      });
      const rep = (await store.list<FakeSales>("sales")).find(
        (s) => s.id === me,
      );
      const name =
        rep?.first_name?.trim() || rep?.last_name?.trim() || "un représentant";
      await store.create("company_notes", {
        company_id: companyId,
        text: `Rappel du ${skipNoteDate(at)} retiré par ${name} — ${label}`,
        date: iso(now()),
        sales_id: me,
        source: "automation",
      });
    },

    async queuePreview(limit, exclude = []) {
      const s = await active();
      const leads = previewQueue(
        await queueLeads(exclude, s?.filter ?? {}),
        await ctx(s?.filter ?? {}),
        limit,
      );
      return Promise.all(leads.map((l) => snapshot(l.id)));
    },

    // GAVION callbacks today: my leads with a callback later today (the demo has no task rule)
    async callbacksLaterToday() {
      const t0 = now();
      const t1 = torontoMidnight(addDays(torontoDate(t0), 1)).getTime();
      const rows = (await store.list<Company>("companies")).filter(
        (c) =>
          c.assigned_sales_id === me &&
          typeof c.next_callback_at === "string" &&
          Date.parse(c.next_callback_at) > t0.getTime() &&
          Date.parse(c.next_callback_at) < t1 &&
          ![
            "do_not_call",
            "customer",
            "bad_number",
            "demo_booked",
            "in_pipeline",
          ].includes(
            // GAVION call-brief-pipeline (my_callbacks_later_today, 83_)
            String(c.prospect_status),
          ),
      );
      return rows
        .map((c) => ({
          companyId: Number(c.id),
          name: String(c.name ?? ""),
          at: String(c.next_callback_at),
        }))
        .sort(
          (a, b) =>
            Date.parse(a.at) - Date.parse(b.at) || a.companyId - b.companyId,
        )
        .slice(0, 20);
    },

    async queueFilterOptions() {
      return queueFilterOptions(await queueLeads(), await ctx({}));
    },

    async todayStats() {
      const dayOf = (d: Date) =>
        d.toLocaleDateString("en-CA", { timeZone: tz });
      const day = dayOf(now());
      // GAVION test mode (SQL my_dial_stats_today): a call on a test lead is not counted
      const test = new Set(
        (await store.list<Company>("companies"))
          .filter((c) => c.is_test === true)
          .map((c) => c.id),
      );
      const mine = (await store.list<CallRow>("calls")).filter(
        (c) =>
          c.sales_id === me &&
          dayOf(new Date(c.dialed_at)) === day &&
          !(c.company_id != null && test.has(c.company_id)),
      );
      return {
        dials: mine.length,
        contacts: mine.filter((c) => c.outcome && HUMAN.includes(c.outcome))
          .length,
        demos: mine.filter((c) => c.outcome === "demo_booked").length,
      };
    },

    // GAVION my-calls: twin of the Supabase query (calls + companies(name) + contacts(name)), newest first.
    async callHistory({
      allReps,
      salesId,
      from,
      to,
      outcomes,
      direction,
      page,
      perPage,
    }) {
      const lo = Date.parse(from);
      const hi = Date.parse(to);
      const none = outcomes.includes("none");
      const codes = outcomes.filter((o) => o !== "none") as string[];
      const keep = (o: CallOutcome | null) =>
        outcomes.length === 0 ||
        (o == null || o === "unlogged" ? none : codes.includes(o));
      const all = (await store.list<CallRow>("calls"))
        .filter((c) => {
          const at = Date.parse(c.dialed_at);
          return (
            at >= lo &&
            at < hi &&
            (allReps && isAdmin
              ? salesId == null || Number(c.sales_id) === salesId
              : Number(c.sales_id) === me) &&
            keep(c.outcome ?? null) &&
            (direction == null || (c.direction ?? "outbound") === direction)
          );
        })
        .sort(
          (a, b) =>
            Date.parse(b.dialed_at) - Date.parse(a.dialed_at) ||
            Number(b.id) - Number(a.id),
        );
      const slice = all.slice((page - 1) * perPage, page * perPage);
      const companies = await store.list<Company>("companies");
      const contacts = slice.some((c) => c.contact_id != null)
        ? await store.list<{
            id: number;
            first_name?: string | null;
            last_name?: string | null;
          }>("contacts")
        : [];
      return {
        total: all.length,
        rows: slice.map((c) => {
          const k =
            c.contact_id != null
              ? contacts.find((x) => Number(x.id) === Number(c.contact_id))
              : undefined;
          return {
            id: Number(c.id),
            company_id: c.company_id ?? null,
            contact_id: c.contact_id ?? null,
            sales_id: Number(c.sales_id),
            direction: c.direction ?? "outbound",
            dialed_at: c.dialed_at,
            outcome: c.outcome ?? null,
            note: c.note ?? null,
            callback_at: c.callback_at ?? null,
            duration_s: c.duration_s ?? null,
            company_name:
              companies.find((x) => Number(x.id) === Number(c.company_id))
                ?.name ?? null,
            contact_name: k ? contactDisplayName(k) || null : null,
          };
        }),
      };
    },

    /** allReps is admin-only (as in SQL); a rep always gets their own calls. */
    async todoCalls({ allReps, salesId }) {
      const companies = await store.list<Company>("companies");
      return (await store.list<CallRow>("calls"))
        .filter(
          (c) =>
            c.outcome === "unlogged" &&
            (allReps && isAdmin
              ? salesId == null || c.sales_id === salesId
              : c.sales_id === me),
        )
        .sort((a, b) => b.dialed_at.localeCompare(a.dialed_at))
        .map((c) => ({
          ...c,
          company_name:
            companies.find((x) => x.id === c.company_id)?.name ?? null,
          company_timezone:
            companies.find((x) => x.id === c.company_id)?.timezone ?? null,
          company_email:
            companies.find((x) => x.id === c.company_id)?.email ?? null,
        }));
    },

    async recentActivity(companyId, limit) {
      const calls = (await store.list<CallRow>("calls"))
        .filter((c) => c.company_id === companyId && c.outcome)
        .map(
          (c): ActivityItem => ({
            kind: "call",
            id: c.id,
            at: c.dialed_at,
            title: String(c.outcome),
            body: c.note,
          }),
        );
      const notes = (
        await store.list<{
          id: number;
          company_id: number;
          date: string;
          text: string;
        }>("company_notes")
      )
        .filter((n) => n.company_id === companyId)
        .map(
          (n): ActivityItem => ({
            kind: "note",
            id: n.id,
            at: n.date,
            title: "note",
            body: n.text,
          }),
        );
      return [...calls, ...notes]
        .sort((a, b) => b.at.localeCompare(a.at))
        .slice(0, limit);
    },

    async deleteDeal(dealId) {
      await store.remove("deals", dealId);
    },

    async getMyPrefs() {
      const row = await store.get<{ prefs?: SalesDialPrefs }>("sales", me);
      return { ...(row?.prefs ?? {}) };
    },

    async saveMyPrefs(patch) {
      const clean = Object.fromEntries(
        Object.entries(patch).filter(([k]) => PREF_KEYS.includes(k)),
      );
      const prefs: Record<string, unknown> = {
        ...(await api.getMyPrefs()),
        ...clean,
      };
      for (const [k, v] of Object.entries(clean))
        if (v === null) delete prefs[k];
      await store.update("sales", me, { prefs });
      return prefs;
    },

    // GAVION X4: twin of set_my_digest_enabled
    async setMyDigestEnabled(enabled) {
      const prefs = { ...(await api.getMyPrefs()), digest_enabled: enabled };
      await store.update("sales", me, { prefs });
      return prefs;
    },

    // GAVION rep-missed-call: twin of set_my_missed_call_email (57_gavion_rep_missed_call.sql)
    async setMyMissedCallEmail(enabled) {
      const prefs = {
        ...(await api.getMyPrefs()),
        missed_call_email: enabled,
      };
      await store.update("sales", me, { prefs });
      return prefs;
    },

    // GAVION inbound calls: twin of public.start_inbound_call (80_gavion_inbound_calls.sql)
    async startInboundCall({ phone, companyId, contactId }) {
      const e164 = normalizePhone(phone);
      if (!e164) throw new DialerApiError("invalid_phone");
      const t = now().getTime();
      const at = iso(now());
      const all = await store.list<CallRow>("calls");
      const mine = all
        .filter(
          (c) =>
            c.sales_id === me &&
            c.direction === "inbound" &&
            c.from_number === e164,
        )
        .sort(
          (a, b) =>
            Date.parse(b.claimed_at ?? b.dialed_at) -
              Date.parse(a.claimed_at ?? a.dialed_at) || b.id - a.id,
        );
      const answeredWithin = (c: CallRow, ms: number) =>
        !!c.claimed_at && t - Date.parse(c.claimed_at) <= ms;
      const companies = await store.list<Company>("companies");
      const contacts = await store.list<InboundContact>("contacts");
      const newCall = (
        company: number | null,
        contact: number | null,
        launched: string | null,
      ) =>
        store.create<CallRow>("calls", {
          company_id: company,
          contact_id: contact,
          sales_id: me,
          dial_session_id: null,
          direction: "inbound",
          to_number: null,
          from_number: e164,
          claimed_at: at,
          dialed_at: at,
          launched_at: launched,
          ended_at: null,
          outcome: null,
          outcome_version: 0,
          outcome_at: null,
          note: null,
          callback_at: null,
          voipms_cdr_id: null,
          deal_id: null,
          duration_s: null,
          disposition: null,
        });

      if (companyId == null) {
        // the answer: a second event for the same call → the same row
        const open = mine.find(
          (c) => !c.ended_at && answeredWithin(c, INBOUND_REUSE_MS),
        );
        if (open)
          return inboundResult(
            open.company_id == null ? "pending" : "reused",
            open,
          ) as Promise<InboundStartResult>;
        const matches = inboundMatches(e164, companies, contacts);
        const ids = [...new Set(matches.map((m) => m.company_id))];
        let company: number | null = null;
        let contact: number | null = null;
        if (ids.length === 1) {
          company = ids[0];
          const withContact = matches.filter((m) => m.contact_id !== null);
          contact = withContact.length === 1 ? withContact[0].contact_id : null;
        }
        const call = await newCall(company, contact, company ? at : null);
        return inboundResult(
          company == null ? "pending" : "created",
          call,
        ) as Promise<InboundStartResult>;
      }

      // the pick
      const company = companyId;
      const contact = contactId ?? null;
      const c = companies.find((x) => Number(x.id) === company);
      if (!c) throw new DialerApiError("not_found");
      if (
        contact !== null &&
        !contacts.some(
          (k) => Number(k.id) === contact && Number(k.company_id) === company,
        )
      )
        throw new DialerApiError("not_found");
      if (
        contact === null &&
        !c.phone_e164 &&
        !String(c.phone_number ?? "").trim() &&
        !companies.some((o) => o.phone_e164 === e164)
      )
        await store.update("companies", company, {
          phone_number: e164,
          phone_e164: e164,
        });
      const latest = mine.find((x) => answeredWithin(x, INBOUND_END_MS));
      if (latest && latest.company_id == null) {
        // pre-deploy review I1: the Granola time is the answer, never the pick
        const call = await store.update<CallRow>("calls", latest.id, {
          company_id: company,
          contact_id: contact,
          launched_at: latest.claimed_at,
        });
        return inboundResult("created", call) as Promise<InboundStartResult>;
      }
      if (latest && latest.outcome === null)
        return inboundResult("reused", latest) as Promise<InboundStartResult>;
      // pre-deploy review minor 1: its result is logged (a stale second tab): never a phantom call
      if (
        latest &&
        t - Date.parse(latest.ended_at ?? latest.claimed_at!) <=
          INBOUND_LOGGED_MS
      )
        throw new DialerApiError("already_logged");
      // no call answered in the CRM: the answered CDR row the sync stored, else a new row
      const cdrRow = mine.find(
        (x) =>
          !x.claimed_at &&
          x.outcome === null &&
          !!x.voipms_cdr_id &&
          x.disposition === "ANSWERED" &&
          (x.billsec_s ?? 0) > 0 && // pre-deploy review minor 7: as SQL and matchCdrs
          t - Date.parse(x.dialed_at) <= INBOUND_END_MS &&
          Date.parse(x.dialed_at) + (x.duration_s ?? 0) * 1000 >=
            t - INBOUND_ADOPT_MS,
      );
      const call = cdrRow
        ? await store.update<CallRow>("calls", cdrRow.id, {
            company_id: company,
            contact_id: contact,
            claimed_at: cdrRow.dialed_at,
            launched_at: cdrRow.dialed_at, // pre-deploy review I1: the answer
          })
        : await newCall(company, contact, at);
      return inboundResult("created", call) as Promise<InboundStartResult>;
    },

    // GAVION inbound calls: twin of public.end_inbound_call
    async endInboundCall(phone) {
      const e164 = normalizePhone(phone);
      if (!e164) throw new DialerApiError("invalid_phone");
      const t = now().getTime();
      const call = (await store.list<CallRow>("calls"))
        .filter(
          (c) =>
            c.sales_id === me &&
            c.direction === "inbound" &&
            c.from_number === e164 &&
            !!c.claimed_at &&
            t - Date.parse(c.claimed_at) <= INBOUND_END_MS,
        )
        .sort(
          (a, b) =>
            Number(!b.ended_at) - Number(!a.ended_at) ||
            Date.parse(b.claimed_at!) - Date.parse(a.claimed_at!) ||
            b.id - a.id,
        )[0];
      if (!call) return { status: "none", phone: e164 };
      if (call.ended_at)
        return inboundResult(
          "already_ended",
          call,
        ) as Promise<InboundEndResult>;
      const saved = await store.update<CallRow>("calls", call.id, {
        ended_at: iso(now()),
        duration_s:
          call.duration_s ??
          Math.max(0, Math.round((t - Date.parse(call.claimed_at!)) / 1000)),
      });
      return inboundResult("ended", saved) as Promise<InboundEndResult>;
    },
  };
  return api;
}
